/**
 * English translation map for Wave A.
 *
 * Wave A only ships a small set of strings; Waves B and C will extend
 * this file with page-specific keys. All other language files must mirror
 * the key set in this file 1:1 - the `Messages` type is derived from
 * this object so TypeScript will fail the build on drift.
 */

/**
 * The `Messages` type is expressed as a plain interface so translation
 * files (like `tr.ts`) can provide different string values. If we used
 * `as const` + `typeof en`, the type would lock onto literal strings and
 * Turkish text would fail to assign.
 */
export interface Messages {
  app: {
    name: string;
    tagline: string;
    loading: string;
  };
  common: {
    back: string;
    next: string;
    cancel: string;
    confirm: string;
    retry: string;
    close: string;
    error: string;
    emptyTitle: string;
    emptyDescription: string;
    tryAgain: string;
    loading: string;
    copy: string;
    copied: string;
  };
  placeholder: {
    title: string;
    description: string;
  };
  error: {
    boundaryTitle: string;
    boundaryDescription: string;
    reload: string;
  };
  nav: {
    home: string;
    deals: string;
    profile: string;
  };
  onboarding: {
    hero: {
      title: string;
      tagline: string;
    };
    feature: {
      nonCustodial: {
        title: string;
        body: string;
      };
      instant: {
        title: string;
        body: string;
      };
      fair: {
        title: string;
        body: string;
      };
    };
    connectWallet: string;
    tonkeeperHint: string;
    skipForNow: string;
    welcomeBack: string;
    unauthorized: {
      title: string;
      body: string;
    };
  };
  dealList: {
    title: string;
    newDeal: string;
    refresh: string;
    filter: {
      role: {
        all: string;
        buyer: string;
        seller: string;
      };
      status: {
        all: string;
        active: string;
        completed: string;
        disputed: string;
        cancelled: string;
      };
    };
    empty: {
      title: string;
      body: string;
      cta: string;
    };
    filtered: {
      empty: string;
      clear: string;
    };
    error: {
      title: string;
    };
    status: {
      created: string;
      funded: string;
      active: string;
      delivered: string;
      completed: string;
      disputed: string;
      resolved: string;
      cancelled: string;
      expired: string;
      refunded: string;
    };
    role: {
      buyer: string;
      seller: string;
    };
  };
  newDeal: {
    title: string;
    stepIndicator: string;
    step: {
      amount: {
        title: string;
        helper: string;
        placeholder: string;
        usdEquivalent: string;
        quickPick: string;
        currencyLabel: string;
        currencyTon: string;
        currencyUsdt: string;
        currencyUsdtSoon: string;
      };
      seller: {
        title: string;
        helper: string;
        placeholder: string;
        usernameUnsupported: string;
        searching: string;
        resolved: string;
        notFound: string;
      };
      details: {
        title: string;
        helper: string;
        titleLabel: string;
        titlePlaceholder: string;
        categoryLabel: string;
        descriptionLabel: string;
        descriptionPlaceholder: string;
        descriptionCount: string;
        addTerms: string;
        hideTerms: string;
        termsLabel: string;
        termsPlaceholder: string;
        timeoutLabel: string;
        timeoutDays: string;
        timeoutHours: string;
        timeoutCombined: string;
      };
      confirm: {
        title: string;
        helper: string;
        summaryAmount: string;
        summarySeller: string;
        summaryTitle: string;
        summaryCategory: string;
        summaryDescription: string;
        summaryTerms: string;
        summaryTimeout: string;
        disclaimer: string;
        noSellerYet: string;
      };
      share: {
        title: string;
        subtitle: string;
        cardAmount: string;
        cardExpires: string;
        shareButton: string;
        copyButton: string;
        copied: string;
        done: string;
        footer: string;
        inviteMessage: string;
      };
    };
    button: {
      continue: string;
      create: string;
      creating: string;
      createInvite: string;
      creatingInvite: string;
      doneShare: string;
    };
    category: {
      freelance: string;
      otc: string;
      nft: string;
      service: string;
      goods: string;
      other: string;
    };
    error: {
      invalidAmount: string;
      invalidSeller: string;
      titleTooShort: string;
      descriptionTooShort: string;
      createFailed: string;
    };
  };
  dealDetail: {
    header: {
      title: string;
      youLabel: string;
      justCreatedBanner: string;
      copied: string;
    };
    roles: {
      buyer: string;
      seller: string;
    };
    details: {
      sectionTitle: string;
      description: string;
      terms: string;
      category: string;
      createdAt: string;
      fundingDeadline: string;
      deliveryDeadline: string;
      contractAddress: string;
      contractNotDeployed: string;
      txDeploy: string;
      txFund: string;
      txRelease: string;
      txRefund: string;
      viewOnExplorer: string;
      noTerms: string;
      none: string;
    };
    counterparty: {
      title: string;
      placeholderUser: string;
    };
    timeline: {
      title: string;
      created: string;
      funded: string;
      active: string;
      delivered: string;
      completed: string;
      disputed: string;
      resolved: string;
      cancelled: string;
    };
    action: {
      fund: string;
      fundWaiting: string;
      fundConfirming: string;
      markDelivered: string;
      markDeliveredConfirm: string;
      markDeliveredTitle: string;
      release: string;
      releaseConfirm: string;
      releaseTitle: string;
      cancel: string;
      cancelConfirm: string;
      cancelTitle: string;
      openDispute: string;
      openDisputeTitle: string;
      openDisputeConfirm: string;
      openDisputeContinue: string;
      waitingForBuyer: string;
      waitingForSeller: string;
      readonly: string;
    };
    disputeBanner: {
      title: string;
      body: string;
      cta: string;
    };
    error: {
      notFound: string;
      notFoundBody: string;
      backToDeals: string;
      network: string;
      stateChanged: string;
      connectWallet: string;
      transactionFailed: string;
      contractMissing: string;
      nonCustodialViolation: string;
      pollTimeout: string;
    };
    success: {
      completedTitle: string;
      completedBody: string;
    };
    countdown: {
      expiresIn: string;
      expired: string;
    };
    deadline: {
      funding: {
        label: string;
        hint: string;
      };
      delivery: {
        label: string;
        hint: string;
      };
      review: {
        label: string;
        hint: string;
      };
      expired: string;
    };
    extension: {
      buyerButton: string;
      sellerButton: string;
      modalTitle: string;
      modalTitleBuyer: string;
      modalTitleSeller: string;
      hoursLabel: string;
      hoursPlaceholder: string;
      reasonLabel: string;
      reasonPlaceholder: string;
      submit: string;
      submitting: string;
      errorInvalidHours: string;
      errorSubmit: string;
      buyerSuccess: string;
      sellerSuccess: string;
      pendingCardTitle: string;
      pendingCardBodyBuyer: string;
      pendingCardBodySeller: string;
      pendingRequestBy: string;
      pendingRequestHours: string;
      pendingRequestReason: string;
      approve: string;
      reject: string;
      approving: string;
      rejecting: string;
      approveSuccess: string;
      rejectSuccess: string;
    };
    note: {
      optional: string;
      placeholder: string;
    };
    reason: {
      optional: string;
      placeholder: string;
    };
  };
  profile: {
    title: string;
    identity: {
      anonymous: string;
      telegramId: string;
      role: {
        user: string;
        arbiter: string;
        admin: string;
      };
      banned: {
        title: string;
        body: string;
      };
    };
    reputation: {
      title: string;
      scoreLabel: string;
      totalDeals: string;
      completed: string;
      disputed: string;
      totalVolume: string;
      footer: string;
    };
    wallet: {
      title: string;
      empty: string;
      connect: string;
      reconnect: string;
      disconnect: string;
      disconnectHint: string;
      disconnectSuccess: string;
      viewOnExplorer: string;
      mismatch: {
        title: string;
        body: string;
        cta: string;
      };
      updateSuccess: string;
      updateFailed: string;
    };
    settings: {
      title: string;
      language: string;
      theme: string;
      themeControlled: string;
      about: string;
      version: string;
      website: string;
      privacy: string;
      terms: string;
    };
    error: {
      loadFailed: string;
      retry: string;
    };
  };
  dispute: {
    title: string;
    backToDeal: string;
    error: {
      notDisputable: string;
      notFound: string;
      loading: string;
    };
    phase: {
      open: {
        title: string;
        stepIndicator: string;
        step: {
          reason: {
            title: string;
            helper: string;
          };
          details: {
            title: string;
            helper: string;
            placeholder: string;
            charCount: string;
          };
          confirm: {
            title: string;
            helper: string;
            disclaimer: string;
            summaryDeal: string;
            summaryReason: string;
          };
        };
        reason: {
          notDelivered: string;
          qualityMismatch: string;
          unreachable: string;
          scopeDisagreement: string;
          other: string;
          otherPlaceholder: string;
        };
        button: {
          continue: string;
          submit: string;
          submitting: string;
        };
        error: {
          minLength: string;
          maxLength: string;
          submitFailed: string;
        };
      };
      view: {
        title: string;
        status: {
          open: string;
          evidence: string;
          review: string;
          resolved: string;
          appealed: string;
          closed: string;
        };
        statusBanner: {
          open: string;
          evidence: string;
          review: string;
          resolved: string;
          appealed: string;
          closed: string;
        };
        originalReason: string;
        backendGap: string;
        backendGapBody: string;
        evidence: {
          title: string;
          empty: string;
          listComingSoon: string;
          submittedBy: string;
          form: {
            title: string;
            typeLabel: string;
            type: {
              text: string;
              link: string;
              image: string;
              file: string;
            };
            contentLabel: string;
            contentPlaceholderText: string;
            contentPlaceholderLink: string;
            contentPlaceholderImage: string;
            contentPlaceholderFile: string;
            submit: string;
            submitting: string;
            errorContent: string;
            errorSubmit: string;
            success: string;
          };
        };
        defenseBanner: {
          title: string;
          body: string;
          remaining: string;
        };
        messages: {
          title: string;
          empty: string;
          placeholder: string;
          send: string;
          sending: string;
          errorSend: string;
          readOnly: string;
          role: {
            buyer: string;
            seller: string;
            arbiter: string;
            admin: string;
          };
          you: string;
        };
        outcome: {
          title: string;
          buyerWins: string;
          sellerWins: string;
          split: string;
          mutualCancel: string;
          buyerAmount: string;
          sellerAmount: string;
          resolutionNote: string;
        };
      };
    };
  };
  invite: {
    title: string;
    subtitle: string;
    backToDeals: string;
    hero: {
      from: string;
      amount: string;
      for: string;
    };
    details: {
      description: string;
      terms: string;
      fundingWindow: string;
      expires: string;
    };
    disclaimer: string;
    button: {
      accept: string;
      accepting: string;
      decline: string;
      connectWallet: string;
    };
    notFound: {
      title: string;
      body: string;
    };
    error: {
      loadFailed: string;
      acceptFailed: string;
      selfInvite: string;
    };
  };
  admin: {
    queue: {
      title: string;
      subtitle: string;
      empty: {
        title: string;
        body: string;
      };
      filedBy: string;
      buyer: string;
      seller: string;
      amount: string;
      reason: string;
      filed: string;
      review: string;
      loadError: string;
      unauthorized: string;
    };
    detail: {
      title: string;
      subtitle: string;
      notFound: string;
      sectionDeal: string;
      sectionParties: string;
      sectionEvidence: string;
      sectionDecide: string;
      noEvidence: string;
      contractAddress: string;
      filedBy: string;
      filedReason: string;
      filedAt: string;
      evidenceFrom: string;
      evidenceFromBuyer: string;
      evidenceFromSeller: string;
      outcome: {
        buyerWins: string;
        sellerWins: string;
        mutualCancel: string;
        split: string;
      };
      noteLabel: string;
      notePlaceholder: string;
      noteRequired: string;
      buyerAmountLabel: string;
      sellerAmountLabel: string;
      splitWarn: string;
      submit: string;
      submitting: string;
      success: string;
      error: string;
      confirmTitle: string;
      confirmBody: string;
      confirmContinue: string;
      backToQueue: string;
    };
    nav: string;
  };
}

export const en: Messages = {
  app: {
    name: '0xCent Escrow',
    tagline: 'Trustless TON escrow inside Telegram',
    loading: 'Loading...',
  },
  common: {
    back: 'Back',
    next: 'Next',
    cancel: 'Cancel',
    confirm: 'Confirm',
    retry: 'Retry',
    close: 'Close',
    error: 'Something went wrong',
    emptyTitle: 'Nothing here yet',
    emptyDescription: 'Come back later or start a new deal.',
    tryAgain: 'Try again',
    loading: 'Loading',
    copy: 'Copy',
    copied: 'Copied',
  },
  placeholder: {
    title: '{{page}} coming soon',
    description:
      'This screen is a Wave A stub. Real content ships in Wave B or C.',
  },
  error: {
    boundaryTitle: 'Unexpected error',
    boundaryDescription:
      'The app hit an unrecoverable error. Try reloading the Mini App from Telegram.',
    reload: 'Reload',
  },
  nav: {
    home: 'Home',
    deals: 'Deals',
    profile: 'Profile',
  },
  onboarding: {
    hero: {
      title: '0xCent',
      tagline: 'Trustless TON escrow, right inside Telegram.',
    },
    feature: {
      nonCustodial: {
        title: 'Non-custodial',
        body: 'Funds stay in the smart contract, never in our hands.',
      },
      instant: {
        title: 'Instant',
        body: 'Deploy, fund, release in under a minute.',
      },
      fair: {
        title: 'Fair',
        body: 'Built-in dispute resolution with on-chain proof.',
      },
    },
    connectWallet: 'Connect Tonkeeper',
    tonkeeperHint: 'Tonkeeper opens automatically. Make sure Testnet is enabled.',
    skipForNow: 'Skip for now',
    welcomeBack: 'Welcome back, {{name}}',
    unauthorized: {
      title: 'Open this app from inside Telegram',
      body: 'This Mini App needs to be launched from a Telegram chat so we can verify who you are.',
    },
  },
  dealList: {
    title: 'My Deals',
    newDeal: 'New Deal',
    refresh: 'Refresh',
    filter: {
      role: {
        all: 'All',
        buyer: 'As buyer',
        seller: 'As seller',
      },
      status: {
        all: 'All',
        active: 'Active',
        completed: 'Completed',
        disputed: 'Disputed',
        cancelled: 'Cancelled',
      },
    },
    empty: {
      title: 'No deals yet',
      body: 'Create your first escrow and start trading safely on TON.',
      cta: 'Create your first deal',
    },
    filtered: {
      empty: 'No deals match this filter',
      clear: 'Clear filters',
    },
    error: {
      title: 'Could not load your deals',
    },
    status: {
      created: 'Created',
      funded: 'Funded',
      active: 'In progress',
      delivered: 'Delivered',
      completed: 'Completed',
      disputed: 'Disputed',
      resolved: 'Resolved',
      cancelled: 'Cancelled',
      expired: 'Expired',
      refunded: 'Refunded',
    },
    role: {
      buyer: 'Buyer',
      seller: 'Seller',
    },
  },
  newDeal: {
    title: 'New Deal',
    stepIndicator: 'Step {{current}} of {{total}}',
    step: {
      amount: {
        title: 'How much?',
        helper: 'Enter the TON amount that will be locked in escrow.',
        placeholder: '0.0',
        usdEquivalent: '~${{amount}}',
        quickPick: 'Quick pick',
        currencyLabel: 'Currency',
        currencyTon: 'TON',
        currencyUsdt: 'USDT',
        currencyUsdtSoon: 'Coming in Phase 4',
      },
      seller: {
        title: "Who's the seller?",
        helper:
          "Send the seller's @username or Telegram numeric ID. They must have opened the bot at least once.",
        placeholder: '@username or 123456789',
        usernameUnsupported:
          'Username lookup is coming soon — for now please paste the numeric Telegram ID.',
        searching: 'Looking up username…',
        resolved: 'Resolved as @{username} ({name})',
        notFound:
          'No user with this username opened the bot yet. Ask them to start the bot first.',
      },
      details: {
        title: 'Deal details',
        helper: 'Describe the work, goods, or service the buyer is paying for.',
        titleLabel: 'Title',
        titlePlaceholder: 'e.g. Logo design for SaaS landing page',
        categoryLabel: 'Category',
        descriptionLabel: 'Description',
        descriptionPlaceholder:
          'Scope, deliverables, deadlines — anything the seller needs to know.',
        descriptionCount: '{{count}} / {{max}}',
        addTerms: 'Add custom terms',
        hideTerms: 'Hide custom terms',
        termsLabel: 'Custom terms (optional)',
        termsPlaceholder:
          'Optional legal-style terms binding both parties for this deal.',
        timeoutLabel: 'Funding timeout',
        timeoutDays: '{{days}} days',
        timeoutHours: '{{hours}} hours',
        timeoutCombined: '{{days}}d {{hours}}h',
      },
      confirm: {
        title: 'Review & create',
        helper: 'Double-check everything before publishing the escrow.',
        summaryAmount: 'Amount',
        summarySeller: 'Seller',
        summaryTitle: 'Title',
        summaryCategory: 'Category',
        summaryDescription: 'Description',
        summaryTerms: 'Custom terms',
        summaryTimeout: 'Funding window',
        disclaimer:
          'Once the invite is accepted the deal is deployed on-chain. You must fund the contract within the timeout window or the deal will expire.',
        noSellerYet:
          "No seller needed yet — you'll share a link with them after creating the invite.",
      },
      share: {
        title: 'Invite ready!',
        subtitle:
          'Share this link with the seller. When they open it, the deal deploys on-chain automatically.',
        cardAmount: 'Amount',
        cardExpires: 'Expires',
        shareButton: 'Share via Telegram',
        copyButton: 'Copy link',
        copied: 'Link copied',
        done: 'Done',
        footer: 'Done? Close this wizard.',
        inviteMessage:
          "Hey — I'm inviting you to a {{amount}} TON escrow on 0xCent: {{title}}. Open this link to review and accept: {{link}}",
      },
    },
    button: {
      continue: 'Continue',
      create: 'Create Deal',
      creating: 'Creating...',
      createInvite: 'Create Invite',
      creatingInvite: 'Creating invite...',
      doneShare: 'Done',
    },
    category: {
      freelance: 'Freelance',
      otc: 'OTC trade',
      nft: 'NFT',
      service: 'Service',
      goods: 'Physical goods',
      other: 'Other',
    },
    error: {
      invalidAmount: 'Enter a valid TON amount (max 9 decimal places).',
      invalidSeller: "Seller's Telegram ID must be numeric.",
      titleTooShort: 'Title must be at least 3 characters.',
      descriptionTooShort: 'Description must be at least 10 characters.',
      createFailed: 'Could not create the deal. Please try again.',
    },
  },
  dealDetail: {
    header: {
      title: 'Deal',
      youLabel: 'You',
      justCreatedBanner: 'Deal created! Share the link with the seller.',
      copied: 'Copied to clipboard',
    },
    roles: {
      buyer: 'Buyer',
      seller: 'Seller',
    },
    details: {
      sectionTitle: 'Details',
      description: 'Description',
      terms: 'Custom terms',
      category: 'Category',
      createdAt: 'Created',
      fundingDeadline: 'Funding deadline',
      deliveryDeadline: 'Delivery deadline',
      contractAddress: 'Escrow contract',
      contractNotDeployed: 'Contract not deployed yet',
      txDeploy: 'Deploy tx',
      txFund: 'Fund tx',
      txRelease: 'Release tx',
      txRefund: 'Refund tx',
      viewOnExplorer: 'View on explorer',
      noTerms: 'No custom terms',
      none: '—',
    },
    counterparty: {
      title: 'Parties',
      placeholderUser: 'User #{{id}}',
    },
    timeline: {
      title: 'Timeline',
      created: 'Deal created',
      funded: 'Funds locked',
      active: 'Work in progress',
      delivered: 'Marked delivered',
      completed: 'Funds released',
      disputed: 'Dispute opened',
      resolved: 'Dispute resolved',
      cancelled: 'Cancelled',
    },
    action: {
      fund: 'Fund Deal',
      fundWaiting: 'Open wallet to fund',
      fundConfirming: 'Confirming on chain...',
      markDelivered: 'Mark Delivered',
      markDeliveredTitle: 'Mark as delivered?',
      markDeliveredConfirm:
        'Confirm that you have completed the work or shipped the goods. The buyer will be notified to release the funds.',
      release: 'Release Funds',
      releaseTitle: 'Release funds to seller?',
      releaseConfirm:
        'This will release the escrowed funds to the seller. This action cannot be undone.',
      cancel: 'Cancel Deal',
      cancelTitle: 'Cancel this deal?',
      cancelConfirm:
        'If the deal is funded, the locked funds will be returned to the buyer. This action cannot be undone.',
      openDispute: 'Open Dispute',
      openDisputeTitle: 'Open a dispute?',
      openDisputeConfirm:
        'Only start a dispute if something is actually wrong. This will freeze the escrow and require an arbiter to decide. If the seller already delivered the work correctly, tap "Release Funds" instead.',
      openDisputeContinue: 'Yes, open dispute',
      waitingForBuyer: 'Waiting for the buyer to fund',
      waitingForSeller: 'Waiting for the seller to deliver',
      readonly: 'This deal is closed',
    },
    disputeBanner: {
      title: 'Dispute In Progress',
      body: 'A dispute has been opened on this deal. Go to the Dispute Center to defend yourself or read messages.',
      cta: 'Go to Dispute Center',
    },
    error: {
      notFound: 'Deal not found',
      notFoundBody: 'This deal does not exist or you do not have access to it.',
      backToDeals: 'Back to Deals',
      network: 'Could not load the deal. Please try again.',
      stateChanged: 'Deal state changed, please refresh.',
      connectWallet: 'Connect your TON wallet to fund this deal.',
      transactionFailed: 'Transaction failed or was rejected.',
      contractMissing: 'Escrow contract is not ready yet. Please refresh in a moment.',
      nonCustodialViolation:
        'Refusing to send funds: the returned address is not the escrow contract.',
      pollTimeout:
        'Funding sent but not yet visible on chain. Refresh in a few seconds.',
    },
    success: {
      completedTitle: 'Deal completed',
      completedBody: 'The funds have been released to the seller.',
    },
    countdown: {
      expiresIn: 'Expires in {{time}}',
      expired: 'Expired',
    },
    deadline: {
      funding: {
        label: 'Funding window',
        hint: 'Buyer must fund the contract before this expires.',
      },
      delivery: {
        label: 'Delivery window',
        hint: 'Seller must deliver before this expires.',
      },
      review: {
        label: 'Review window',
        hint: 'Buyer must release or open a dispute before this auto-releases.',
      },
      expired: 'Window closed',
    },
    extension: {
      buyerButton: 'Extend deadline',
      sellerButton: 'Request more time',
      modalTitle: 'Extend deadline',
      modalTitleBuyer: 'Give the seller more time',
      modalTitleSeller: 'Request more time from the buyer',
      hoursLabel: 'Additional hours',
      hoursPlaceholder: 'e.g. 24',
      reasonLabel: 'Reason (optional)',
      reasonPlaceholder: 'Why do you need more time?',
      submit: 'Submit',
      submitting: 'Submitting…',
      errorInvalidHours: 'Please enter a whole number of hours between 1 and 168.',
      errorSubmit: 'Could not submit the request. Please try again.',
      buyerSuccess: 'Deadline extended.',
      sellerSuccess: 'Request sent to the buyer.',
      pendingCardTitle: 'Extension request pending',
      pendingCardBodyBuyer: 'The seller is asking for more time. Review and decide.',
      pendingCardBodySeller: 'Waiting for the buyer to approve your request.',
      pendingRequestBy: 'Asked by',
      pendingRequestHours: '+{{hours}} hours',
      pendingRequestReason: 'Reason',
      approve: 'Approve',
      reject: 'Reject',
      approving: 'Approving…',
      rejecting: 'Rejecting…',
      approveSuccess: 'Extension approved.',
      rejectSuccess: 'Request rejected.',
    },
    note: {
      optional: 'Note (optional)',
      placeholder: 'Anything you want the buyer to know...',
    },
    reason: {
      optional: 'Reason (optional)',
      placeholder: 'Why are you cancelling this deal?',
    },
  },
  profile: {
    title: 'Profile',
    identity: {
      anonymous: 'Anonymous user',
      telegramId: 'Telegram ID',
      role: {
        user: 'User',
        arbiter: 'Arbiter',
        admin: 'Admin',
      },
      banned: {
        title: 'Account banned',
        body: 'Your access to 0xCent has been suspended. Reason: {{reason}}',
      },
    },
    reputation: {
      title: 'Reputation',
      scoreLabel: '{{score}} / 100',
      totalDeals: 'Total deals',
      completed: 'Completed',
      disputed: 'Disputed',
      totalVolume: 'Total volume',
      footer: 'Reputation is calculated from successful and disputed deals.',
    },
    wallet: {
      title: 'TON Wallet',
      empty: 'No wallet connected',
      connect: 'Connect Tonkeeper',
      reconnect: 'Change wallet',
      disconnect: 'Disconnect',
      disconnectHint: 'Removes the wallet from both Tonkeeper and your profile.',
      disconnectSuccess: 'Wallet disconnected',
      viewOnExplorer: 'View on Tonviewer',
      mismatch: {
        title: 'Wallet mismatch',
        body: "Your connected wallet doesn't match your profile.",
        cta: 'Update profile',
      },
      updateSuccess: 'Wallet updated',
      updateFailed: 'Could not update the wallet. Please try again.',
    },
    settings: {
      title: 'Settings',
      language: 'Language',
      theme: 'Theme',
      themeControlled: 'Controlled by Telegram',
      about: 'About',
      version: 'Version {{version}}',
      website: 'Website',
      privacy: 'Privacy policy',
      terms: 'Terms of service',
    },
    error: {
      loadFailed: 'Could not load your profile.',
      retry: 'Try again',
    },
  },
  dispute: {
    title: 'Dispute',
    backToDeal: 'Back to deal',
    error: {
      notDisputable:
        'This deal is not in a state that allows disputes. Disputes can only be opened on active or delivered deals.',
      notFound: 'Dispute not found',
      loading: 'Could not load the dispute. Please try again.',
    },
    phase: {
      open: {
        title: 'Open a dispute',
        stepIndicator: 'Step {{current}} of {{total}}',
        step: {
          reason: {
            title: 'Why are you opening a dispute?',
            helper:
              'Pick the closest reason. You can add details on the next step.',
          },
          details: {
            title: 'Tell us what happened',
            helper:
              'Describe the issue in your own words. Be specific — the arbiter will use this to understand your side.',
            placeholder:
              'Explain what went wrong, when it happened, and what you have already tried...',
            charCount: '{{count}} / {{max}}',
          },
          confirm: {
            title: 'Confirm dispute',
            helper: 'Review your reason before submitting.',
            disclaimer:
              'Opening a dispute will freeze the contract on-chain. An arbiter will review the evidence from both sides before deciding how the funds are split. This action cannot be undone.',
            summaryDeal: 'Deal',
            summaryReason: 'Reason',
          },
        },
        reason: {
          notDelivered: "Seller didn't deliver",
          qualityMismatch: 'Quality not as described',
          unreachable: 'Counterparty is unreachable',
          scopeDisagreement: 'Scope disagreement',
          other: 'Other reason',
          otherPlaceholder: 'Briefly describe your reason...',
        },
        button: {
          continue: 'Continue',
          submit: 'Submit dispute',
          submitting: 'Submitting...',
        },
        error: {
          minLength: 'Please provide at least 10 characters.',
          maxLength: 'Reason cannot exceed {{max}} characters.',
          submitFailed: 'Could not open the dispute. Please try again.',
        },
      },
      view: {
        title: 'Dispute',
        status: {
          open: 'Open',
          evidence: 'Collecting evidence',
          review: 'Under review',
          resolved: 'Resolved',
          appealed: 'Appealed',
          closed: 'Closed',
        },
        statusBanner: {
          open: 'A dispute has been opened on this deal. The arbiter will review it shortly.',
          evidence:
            'Evidence collection window is open. Submit any proof you have to support your case.',
          review: 'The arbiter is reviewing this dispute.',
          resolved: 'The arbiter has resolved this dispute.',
          appealed: 'This dispute has been appealed and is awaiting a second review.',
          closed: 'This dispute is closed.',
        },
        originalReason: 'Original reason',
        backendGap: 'Dispute details unavailable',
        backendGapBody:
          'The deal has been frozen but the full dispute record is being prepared. Refresh in a moment, or come back from the deal page once the arbiter has been assigned.',
        evidence: {
          title: 'Evidence',
          empty: 'No evidence has been submitted yet.',
          listComingSoon: 'Evidence list coming soon',
          submittedBy: 'Submitted by {{actor}}',
          form: {
            title: 'Submit evidence',
            typeLabel: 'Type',
            type: {
              text: 'Text',
              link: 'Link',
              image: 'Image URL',
              file: 'File URL',
            },
            contentLabel: 'Content',
            contentPlaceholderText:
              'Write your statement, conversation transcript, or details...',
            contentPlaceholderLink: 'https://example.com/proof',
            contentPlaceholderImage: 'https://example.com/screenshot.png',
            contentPlaceholderFile: 'https://example.com/file.pdf',
            submit: 'Submit evidence',
            submitting: 'Submitting...',
            errorContent: 'Please provide content for your evidence.',
            errorSubmit: 'Could not submit evidence. Please try again.',
            success: 'Evidence submitted',
          },
        },
        defenseBanner: {
          title: 'A dispute was opened against you',
          body: 'You can defend yourself here. You have 48 hours to submit evidence and messages.',
          remaining: 'Time remaining',
        },
        messages: {
          title: 'Messages',
          empty: 'No messages yet. Write the first message to start your defense.',
          placeholder: 'Write your message...',
          send: 'Send',
          sending: 'Sending...',
          errorSend: 'Could not send message. Please try again.',
          readOnly: 'This dispute has been resolved — messages are read-only.',
          role: {
            buyer: 'Buyer',
            seller: 'Seller',
            arbiter: 'Arbiter',
            admin: 'Admin',
          },
          you: 'You',
        },
        outcome: {
          title: 'Outcome',
          buyerWins: 'Buyer wins — full refund',
          sellerWins: 'Seller wins — funds released',
          split: 'Split decision',
          mutualCancel: 'Mutual cancellation',
          buyerAmount: 'Buyer receives',
          sellerAmount: 'Seller receives',
          resolutionNote: 'Arbiter note',
        },
      },
    },
  },
  invite: {
    title: 'Deal invite',
    subtitle: 'Review this invite before you accept.',
    backToDeals: 'Back to deals',
    hero: {
      from: 'From',
      amount: 'Amount',
      for: 'For',
    },
    details: {
      description: 'Description',
      terms: 'Custom terms',
      fundingWindow: '{{hours}}h funding window',
      expires: 'Expires {{relative}}',
    },
    disclaimer:
      'When you accept, the escrow contract deploys on-chain. The buyer must fund it within the funding window or the deal expires automatically.',
    button: {
      accept: 'Accept invite',
      accepting: 'Accepting…',
      decline: 'Decline',
      connectWallet: 'Connect wallet',
    },
    notFound: {
      title: 'Invite no longer valid',
      body: 'This invite has expired, been accepted, or never existed. Ask the buyer to send you a fresh link.',
    },
    error: {
      loadFailed: 'Could not load the invite. Please try again.',
      acceptFailed: 'Could not accept the invite. Please try again.',
      selfInvite:
        "You can't accept your own invite. Share the link with another Telegram user.",
    },
  },
  admin: {
    queue: {
      title: 'Dispute Queue',
      subtitle: 'Disputes waiting for arbiter review',
      empty: {
        title: 'No active disputes',
        body: 'Every dispute has been resolved. New ones will appear here automatically.',
      },
      filedBy: 'Filed by',
      buyer: 'Buyer',
      seller: 'Seller',
      amount: 'Amount',
      reason: 'Reason',
      filed: 'Filed',
      review: 'Review',
      loadError: 'Could not load the dispute queue. Please try again.',
      unauthorized: 'Only arbiters and admins can access this page.',
    },
    detail: {
      title: 'Resolve Dispute',
      subtitle: 'Review evidence and decide an outcome',
      notFound: 'This dispute no longer exists or you do not have access to it.',
      sectionDeal: 'Deal Snapshot',
      sectionParties: 'Parties',
      sectionEvidence: 'Evidence',
      sectionDecide: 'Decide Outcome',
      noEvidence: 'Neither party has submitted evidence yet.',
      contractAddress: 'Contract',
      filedBy: 'Filed by',
      filedReason: 'Reason',
      filedAt: 'Filed at',
      evidenceFrom: 'From',
      evidenceFromBuyer: 'Buyer',
      evidenceFromSeller: 'Seller',
      outcome: {
        buyerWins: 'Refund Buyer (full)',
        sellerWins: 'Release to Seller',
        mutualCancel: 'Mutual Cancel (refund buyer)',
        split: 'Split (custom amounts)',
      },
      noteLabel: 'Resolution note (visible in audit log)',
      notePlaceholder: 'Briefly justify the decision so the audit trail makes sense later.',
      noteRequired: 'Please add a short note explaining the decision.',
      buyerAmountLabel: 'Buyer amount (TON)',
      sellerAmountLabel: 'Seller amount (TON)',
      splitWarn:
        'Split amounts must sum to less than the deal total — the contract subtracts the dispute fee.',
      submit: 'Execute Resolution',
      submitting: 'Resolving on-chain…',
      success: 'Dispute resolved',
      error: 'Could not resolve the dispute. Check the logs and try again.',
      confirmTitle: 'Resolve this dispute?',
      confirmBody:
        'This will send an op message to the escrow contract and route the funds. The action cannot be undone.',
      confirmContinue: 'Yes, resolve now',
      backToQueue: 'Back to queue',
    },
    nav: 'Dispute queue',
  },
};
